import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import Navigation from "@/components/Navigation";
import { BookOpen, Clock, CheckCircle2, Lock, Unlock, ChevronDown, ChevronUp, Sprout, Zap, Compass, Flame, Clock3, BrainCircuit, Gamepad2, Feather, Heart, Sparkles } from "lucide-react";

// Block definitions with icons (white/subtle)
const BLOCKS_CONFIG = {
  1: { icon: Sprout, title_es: "Fundamentos del Autoconocimiento", title_ca: "Fonaments de l'Autoconeixement", desc_es: "Introducción al camino emocional", desc_ca: "Introducció al camí emocional" },
  2: { icon: Zap, title_es: "Energía y Vibración", title_ca: "Energia i Vibració", desc_es: "Todo es energía en movimiento", desc_ca: "Tot és energia en moviment" },
  3: { icon: Compass, title_es: "Emociones como Brújula", title_ca: "Emocions com a Brúixola", desc_es: "Las emociones son tu guía interior", desc_ca: "Les emocions són la teva guia interior" },
  4: { icon: Flame, title_es: "Transformar Emociones Difíciles", title_ca: "Transformar Emocions Difícils", desc_es: "Alquimia emocional", desc_ca: "Alquímia emocional" },
  5: { icon: Clock3, title_es: "El Poder del Presente", title_ca: "El Poder del Present", desc_es: "Vivir en el ahora", desc_ca: "Viure en l'ara" },
  6: { icon: BrainCircuit, title_es: "Pensamientos Creadores", title_ca: "Pensaments Creadors", desc_es: "El poder de la mente", desc_ca: "El poder de la ment" },
  7: { icon: Gamepad2, title_es: "Tú Eres el Creador", title_ca: "Tu Ets el Creador", desc_es: "Responsabilidad y poder personal", desc_ca: "Responsabilitat i poder personal" },
  8: { icon: Feather, title_es: "Sanación y Liberación", title_ca: "Sanació i Alliberament", desc_es: "Soltar y sanar", desc_ca: "Deixar anar i sanar" },
  9: { icon: Heart, title_es: "El Amor como Frecuencia", title_ca: "L'Amor com a Freqüència", desc_es: "Vivir desde el corazón", desc_ca: "Viure des del cor" },
  10: { icon: Sparkles, title_es: "Integración y Maestría", title_ca: "Integració i Maestria", desc_es: "El camino del alma", desc_ca: "El camí de l'ànima" }
};

const Lessons = () => {
  const { language } = useContext(AuthContext);
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [stats, setStats] = useState({ total: 0, completed: 0, blocksCompleted: 0, totalBlocks: 10 });
  const [loading, setLoading] = useState(true);
  const [expandedBlocks, setExpandedBlocks] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem("token");
      const headers = { Authorization: `Bearer ${token}` };

      const response = await axios.get(`${API}/lessons/blocks`, { headers });
      
      setBlocks(response.data.blocks);
      setStats({
        total: response.data.total_lessons,
        completed: response.data.completed_count,
        blocksCompleted: response.data.blocks_completed,
        totalBlocks: response.data.total_blocks
      });
      
      // All blocks start closed
      setExpandedBlocks({});
    } catch (error) {
      console.error("Error fetching lessons:", error);
      toast.error("Error al cargar lecciones");
    } finally {
      setLoading(false);
    }
  };

  const t = (es, ca) => language === "ca" ? ca : es;

  const toggleBlock = (blockNumber) => {
    setExpandedBlocks(prev => ({
      ...prev,
      [blockNumber]: !prev[blockNumber]
    }));
  };

  const handleLessonClick = (lesson, blockUnlocked) => {
    if (!blockUnlocked) {
      toast.info(t(
        "Completa el bloque anterior para desbloquear este",
        "Completa el bloc anterior per desbloquejar aquest"
      ));
      return;
    }
    navigate(`/lessons/${lesson.id}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
        <Navigation />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="animate-pulse text-teal-300 text-xl">{t("Cargando...", "Carregant...")}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-teal-950">
      <Navigation />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div data-testid="lessons-header" className="mb-8 fade-in">
          <h1 className="text-4xl sm:text-5xl font-bold mb-4 glow-text">
            {t("Lecciones", "Lliçons")}
          </h1>
          <p className="text-lg text-gray-300 mb-4">
            {t("Profundiza en tu camino de autoconocimiento", "Aprofundeix en el teu camí d'autoconeixement")}
          </p>
          
          {/* Progress Stats */}
          <div className="flex flex-wrap gap-4 text-sm">
            <div className="glass rounded-xl px-4 py-2 border border-teal-400/20">
              <span className="text-gray-400">{t("Lecciones", "Lliçons")}: </span>
              <span className="text-teal-300 font-semibold">{stats.completed}/{stats.total}</span>
            </div>
            <div className="glass rounded-xl px-4 py-2 border border-teal-400/20">
              <span className="text-gray-400">{t("Bloques", "Blocs")}: </span>
              <span className="text-teal-300 font-semibold">{stats.blocksCompleted}/{stats.totalBlocks}</span>
            </div>
          </div>
        </div>

        {/* Info Box */}
        <div className="glass rounded-xl p-4 border border-teal-400/10 mb-8 fade-in" style={{animationDelay: '0.05s'}}>
          <p className="text-gray-300 text-sm">
            <span className="text-teal-300">💡</span>{" "}
            {t(
              "Completa cada bloque para desbloquear el siguiente. Tu ritmo, tu camino.",
              "Completa cada bloc per desbloquejar el següent. El teu ritme, el teu camí."
            )}
          </p>
        </div>

        {/* Blocks List */}
        <div data-testid="blocks-list" className="space-y-4">
          {blocks.map((block, blockIndex) => {
            const config = BLOCKS_CONFIG[block.block_number] || {};
            const isExpanded = expandedBlocks[block.block_number];
            const isLocked = !block.unlocked;
            const isCompleted = block.completed;
            
            return (
              <div
                key={block.block_number}
                data-testid={`block-${block.block_number}`}
                className={`card glass rounded-2xl overflow-hidden fade-in transition-all ${
                  isLocked 
                    ? 'opacity-60 border-gray-700/30' 
                    : isCompleted
                      ? 'border-teal-400/20'
                      : 'border-purple-400/20'
                }`}
                style={{ animationDelay: `${blockIndex * 0.08}s` }}
              >
                {/* Block Header */}
                <div 
                  className={`p-5 cursor-pointer flex items-center justify-between gap-4 ${
                    isLocked ? '' : 'hover:bg-white/5'
                  }`}
                  onClick={() => !isLocked && toggleBlock(block.block_number)}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${
                      isLocked 
                        ? 'bg-gray-800/50' 
                        : isCompleted
                          ? 'bg-gradient-to-br from-teal-500/30 to-teal-700/30'
                          : 'bg-gradient-to-br from-purple-500/30 to-purple-700/30'
                    }`}>
                      {isLocked ? <Lock className="w-6 h-6 text-gray-500" /> : <config.icon className="w-6 h-6 text-gray-300" />}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          isLocked 
                            ? 'bg-gray-700 text-gray-400'
                            : isCompleted
                              ? 'bg-teal-500/20 text-teal-300'
                              : 'bg-purple-500/20 text-purple-300'
                        }`}>
                          {t("Bloque", "Bloc")} {block.block_number}
                        </span>
                        {isCompleted && <CheckCircle2 className="w-4 h-4 text-teal-400" />}
                      </div>
                      <h3 className={`text-lg font-semibold mt-1 ${isLocked ? 'text-gray-400' : 'text-white'}`}>
                        {language === "ca" ? config.title_ca : config.title_es}
                      </h3>
                      <p className={`text-sm ${isLocked ? 'text-gray-500' : 'text-gray-400'}`}>
                        {language === "ca" ? config.desc_ca : config.desc_es}
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <span className={`text-sm ${isLocked ? 'text-gray-500' : 'text-gray-400'}`}>
                        {block.completed_count}/{block.lessons.length}
                      </span>
                    </div>
                    {!isLocked && (
                      isExpanded 
                        ? <ChevronUp className="w-5 h-5 text-gray-400" />
                        : <ChevronDown className="w-5 h-5 text-gray-400" />
                    )}
                  </div>
                </div>

                {/* Block Lessons (Expanded) */}
                {isExpanded && !isLocked && (
                  <div className="border-t border-slate-700/50 bg-slate-900/30">
                    {block.lessons.map((lesson, lessonIndex) => {
                      const content = language === "ca" ? lesson.content_ca : lesson.content_es;
                      const preview = content ? content.substring(0, 150) + "..." : "";
                      
                      return (
                        <div
                          key={lesson.id}
                          data-testid={`lesson-${lesson.id}`}
                          className="p-4 border-b border-slate-700/30 last:border-b-0 hover:bg-white/5 cursor-pointer transition-colors"
                          onClick={() => handleLessonClick(lesson, block.unlocked)}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0 ${
                              lesson.completed 
                                ? 'bg-teal-500/30 text-teal-300' 
                                : 'bg-slate-700 text-gray-400'
                            }`}>
                              {lesson.completed ? <CheckCircle2 className="w-4 h-4" /> : lessonIndex + 1}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className={`font-medium ${lesson.completed ? 'text-teal-200' : 'text-gray-200'}`}>
                                {language === "ca" ? lesson.title_ca : lesson.title_es}
                              </h4>
                              <p className="text-sm text-gray-500 mt-1 line-clamp-2">
                                {preview}
                              </p>
                              <div className="flex items-center gap-2 mt-2">
                                <Clock className="w-3 h-3 text-gray-500" />
                                <span className="text-xs text-gray-500">{lesson.duration_minutes} min</span>
                              </div>
                            </div>
                            {lesson.completed && (
                              <CheckCircle2 className="w-5 h-5 text-teal-400 flex-shrink-0" />
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Locked Message */}
                {isLocked && (
                  <div className="px-5 pb-4">
                    <p className="text-sm text-gray-500 italic">
                      {t(
                        "Completa el bloque anterior para desbloquear",
                        "Completa el bloc anterior per desbloquejar"
                      )}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Lessons;
